import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { InheritableField } from './inheritable-field'

type Inheritable<T> = { inherited: boolean; value: T | null } | undefined

export interface AdvancedBlob {
  top_p?: Inheritable<number>
  frequency_penalty?: Inheritable<number>
  presence_penalty?: Inheritable<number>
  stop?: Inheritable<string[]>
  seed?: Inheritable<number>
}

export interface AdvancedParamsSectionProps {
  advanced: AdvancedBlob
  inherited: {
    top_p?: number
    frequency_penalty?: number
    presence_penalty?: number
    stop?: string[]
    seed?: number
  }
  onChange: (next: AdvancedBlob) => void
  /** Hide fields that the current provider does not accept. */
  allowed?: Partial<Record<keyof AdvancedBlob, boolean>>
  disabled?: boolean
}

const DEFAULTS: Required<
  Pick<
    AdvancedParamsSectionProps['inherited'],
    'top_p' | 'frequency_penalty' | 'presence_penalty'
  >
> = {
  top_p: 1,
  frequency_penalty: 0,
  presence_penalty: 0,
}

/**
 * Phase 4 plan 04-05: collapsible wrapping 5 InheritableField<T> instances for
 * top_p / frequency_penalty / presence_penalty / stop / seed (CONTEXT D-15).
 *
 * Respects provider capability whitelist via `allowed` — pass `{stop:false}`
 * to hide stop sequences for a provider that does not support them, etc.
 */
export function AdvancedParamsSection({
  advanced,
  inherited,
  onChange,
  allowed,
  disabled,
}: AdvancedParamsSectionProps) {
  const { t } = useTranslation('workflows')
  const [open, setOpen] = useState(false)
  const show = (k: keyof AdvancedBlob) => allowed?.[k] !== false

  const patchField = <K extends keyof AdvancedBlob>(
    key: K,
    entry: AdvancedBlob[K]
  ) => onChange({ ...advanced, [key]: entry })

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        type="button"
        className="flex items-center gap-2 text-sm font-semibold text-gray-200 hover:text-white transition-colors"
      >
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
        />
        {t('llmConfig.advanced.title')}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4 space-y-4">
        {show('top_p') && (
          <InheritableField<number>
            label={t('llmConfig.advanced.topP')}
            inheritedValue={inherited.top_p ?? DEFAULTS.top_p}
            value={advanced.top_p?.value ?? null}
            inherited={advanced.top_p?.inherited ?? true}
            onChange={(val, isInh) =>
              patchField('top_p', { inherited: isInh, value: isInh ? null : val })
            }
            disabled={disabled}
            renderInput={({ value, onChange, disabled: d }) => (
              <Input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={value}
                disabled={d}
                onChange={(e) => onChange(Number(e.target.value))}
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
              />
            )}
          />
        )}
        {show('frequency_penalty') && (
          <InheritableField<number>
            label={t('llmConfig.advanced.frequencyPenalty')}
            inheritedValue={
              inherited.frequency_penalty ?? DEFAULTS.frequency_penalty
            }
            value={advanced.frequency_penalty?.value ?? null}
            inherited={advanced.frequency_penalty?.inherited ?? true}
            onChange={(val, isInh) =>
              patchField('frequency_penalty', {
                inherited: isInh,
                value: isInh ? null : val,
              })
            }
            disabled={disabled}
            renderInput={({ value, onChange, disabled: d }) => (
              <Input
                type="number"
                min={-2}
                max={2}
                step={0.1}
                value={value}
                disabled={d}
                onChange={(e) => onChange(Number(e.target.value))}
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
              />
            )}
          />
        )}
        {show('presence_penalty') && (
          <InheritableField<number>
            label={t('llmConfig.advanced.presencePenalty')}
            inheritedValue={
              inherited.presence_penalty ?? DEFAULTS.presence_penalty
            }
            value={advanced.presence_penalty?.value ?? null}
            inherited={advanced.presence_penalty?.inherited ?? true}
            onChange={(val, isInh) =>
              patchField('presence_penalty', {
                inherited: isInh,
                value: isInh ? null : val,
              })
            }
            disabled={disabled}
            renderInput={({ value, onChange, disabled: d }) => (
              <Input
                type="number"
                min={-2}
                max={2}
                step={0.1}
                value={value}
                disabled={d}
                onChange={(e) => onChange(Number(e.target.value))}
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
              />
            )}
          />
        )}
        {show('stop') && (
          <div className="space-y-1">
            <InheritableField<string[]>
              label={t('llmConfig.advanced.stop')}
              inheritedValue={inherited.stop ?? []}
              value={advanced.stop?.value ?? null}
              inherited={advanced.stop?.inherited ?? true}
              formatDisplay={(v) => (v.length ? v.join(', ') : '—')}
              onChange={(val, isInh) =>
                patchField('stop', {
                  inherited: isInh,
                  value: isInh ? null : val,
                })
              }
              disabled={disabled}
              renderInput={({ value, onChange, disabled: d }) => (
                <Textarea
                  value={value.join('\n')}
                  disabled={d}
                  onChange={(e) =>
                    onChange(
                      e.target.value
                        .split('\n')
                        .map((s) => s)
                        .filter((s) => s.length > 0)
                        .slice(0, 4)
                    )
                  }
                  placeholder={t('llmConfig.advanced.stopHint')}
                  className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
                />
              )}
            />
            <p className="text-xs text-gray-500">
              {t('llmConfig.advanced.stopHint')}
            </p>
          </div>
        )}
        {show('seed') && (
          <div className="space-y-1">
            <InheritableField<number>
              label={t('llmConfig.advanced.seed')}
              inheritedValue={inherited.seed ?? 0}
              value={advanced.seed?.value ?? null}
              inherited={advanced.seed?.inherited ?? true}
              onChange={(val, isInh) =>
                patchField('seed', {
                  inherited: isInh,
                  value: isInh ? null : val,
                })
              }
              disabled={disabled}
              renderInput={({ value, onChange, disabled: d }) => (
                <Input
                  type="number"
                  step={1}
                  value={value}
                  disabled={d}
                  onChange={(e) =>
                    onChange(Number.parseInt(e.target.value, 10) || 0)
                  }
                  className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
                />
              )}
            />
            <p className="text-xs text-gray-500">
              {t('llmConfig.advanced.seedHint')}
            </p>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}
